import { calendarWithKoreanHolidays } from "./calendar";
import {
  bullet,
  checkItem,
  docLink,
  fileAttachment,
  heading,
  imageAttachment,
  linkAttachment,
  paragraph,
  paragraphMention,
  quoteBlock,
  sampleDoc,
} from "./sample-documents";
import type {
  CostOptions,
  Dependency,
  DependencyType,
  DocumentLink,
  ExecutionTask,
  PlanDocument,
  Project,
  ProjectBudget,
  ProjectRecord,
  ProjectResourceAssignment,
  SolutionItem,
  Task,
} from "./types";

function costOptions(): CostOptions {
  return {
    workingDaysPerMonth: 21,
    includeOverhead: true,
    overheadRatio: 1.1,
    includeTechnicalFee: true,
    technicalFeeRatio: 0.2,
    hideRatesByDefault: false,
  };
}

function schedule(
  phases: Array<{ id: string; name: string; percent?: number; leaves: Array<Partial<Task> & { id: string; name: string; duration: number }> }>,
  links: Array<[string, string, DependencyType?, number?]>,
  prefix: string,
): { tasks: Task[]; dependencies: Dependency[] } {
  const tasks: Task[] = [];
  for (const phase of phases) {
    tasks.push({
      id: phase.id,
      parentId: null,
      name: phase.name,
      duration: 0,
      percentComplete: phase.percent ?? 0,
      progressMethod: "percent",
    });
    for (const leaf of phase.leaves) {
      tasks.push({ parentId: phase.id, percentComplete: 0, progressMethod: "percent", ...leaf });
    }
  }
  const dependencies = links.map(([predecessorId, successorId, type, lag], index) => ({
    id: `${prefix}_d${index + 1}`,
    predecessorId,
    successorId,
    type: type ?? "FS",
    lag: lag ?? 0,
  }));
  return { tasks, dependencies };
}

function exec(id: string, title: string, at: string, extra: Partial<ExecutionTask>): ExecutionTask {
  return {
    id,
    title,
    assignees: [],
    linkedTaskIds: [],
    labelIds: [],
    checklist: [],
    attachments: [],
    comments: [],
    activity: [],
    customFields: {},
    order: 0,
    createdAt: at,
    updatedAt: at,
    ...extra,
  };
}

function record(
  id: string,
  kind: ProjectRecord["kind"],
  seq: number,
  title: string,
  at: string,
  actor: string,
  extra: Partial<ProjectRecord>,
): ProjectRecord {
  return {
    id,
    kind,
    seq,
    title,
    author: actor,
    status: "open",
    assignees: [],
    impacts: [],
    sections: {},
    linkedTaskIds: [],
    linkedExecutionTaskIds: [],
    linkedRecordIds: [],
    attachments: [],
    comments: [],
    activity: [{ id: `${id}_ac`, at, actor, message: "생성" }],
    createdAt: at,
    updatedAt: at,
    ...extra,
  };
}

function finish(
  partial: Omit<Project, "calendar" | "baselines" | "activeBaselineId" | "criticalThreshold" | "updatedAt">,
): Project {
  return {
    ...partial,
    calendar: calendarWithKoreanHolidays(),
    baselines: [],
    activeBaselineId: null,
    criticalThreshold: 0,
    updatedAt: "2026-10-20T09:00:00.000Z",
  };
}

/** 웨이브엑스 SI의 두 번째 플랜. ERP 구축과 사람을 나누되 최기획만 양쪽 모두 씁니다. */
export function createSampleQuoteProject(): Project {
  const { tasks, dependencies } = schedule(
    [
      {
        id: "q_p1",
        name: "범위 확정",
        percent: 70,
        leaves: [
          { id: "q_t1", name: "견적 항목 인터뷰", duration: 5, percentComplete: 100, assignee: "최기획", assignedResourceId: "res_choi_plan", plannedMM: 0.25, deliverable: "항목 목록 v1" },
          { id: "q_t2", name: "변경 계약 경계 정리", duration: 4, percentComplete: 55, assignee: "나", assignedResourceId: "res_me", plannedMM: 0.2 },
          { id: "q_m1", name: "범위 확정", duration: 0, percentComplete: 100 },
        ],
      },
      {
        id: "q_p2",
        name: "견적 엔진",
        percent: 15,
        leaves: [
          { id: "q_t3", name: "단가 테이블 설계", duration: 8, percentComplete: 25, assignee: "송DB", assignedResourceId: "res_song_dba", plannedMM: 0.4 },
          { id: "q_t4", name: "견적서 양식", duration: 6, percentComplete: 0, assignee: "최기획", assignedResourceId: "res_choi_plan", plannedMM: 0.3 },
          { id: "q_t5", name: "승인 알림", duration: 4, percentComplete: 0, assignee: "강인프라", assignedResourceId: "res_kang_infra", plannedMM: 0.2 },
          { id: "q_m2", name: "엔진 검수", duration: 0 },
        ],
      },
      {
        id: "q_p3",
        name: "파일럿",
        leaves: [
          { id: "q_t6", name: "샘플 견적 3건 대조", duration: 5, assignee: "오품질", assignedResourceId: "res_oh_qa", plannedMM: 0.25, constraint: { type: "SNET", date: "2026-12-01" } },
          { id: "q_t7", name: "파일럿 오픈", duration: 0, deadline: "2027-01-29", assignee: "나", assignedResourceId: "res_me" },
        ],
      },
    ],
    [
      ["q_t1", "q_t2"],
      ["q_t2", "q_m1"],
      ["q_m1", "q_t3"],
      ["q_t3", "q_t4"],
      ["q_t3", "q_t5", "SS", 2],
      ["q_t4", "q_m2"],
      ["q_t5", "q_m2"],
      ["q_m2", "q_t6"],
      ["q_t6", "q_t7"],
    ],
    "q",
  );
  const at = "2026-10-12T01:00:00.000Z";
  return finish({
    id: "sample-erp-quote",
    name: "견적 자동화",
    start: "2026-10-06",
    statusDate: "2026-10-20",
    tasks,
    dependencies,
    executionTasks: [
      exec("q_x_scope", "견적 범위 메모 정리", at, {
        status: "inProgress",
        priority: "important",
        bucketId: "b_week",
        assignees: ["나"],
        start: "2026-10-19",
        due: "2026-10-19",
        linkedTaskIds: ["q_t2"],
        labelIds: ["l_scope"],
        order: 0,
      }),
      exec("q_x_send", "변경 항목 목록 보내기", at, {
        status: "notStarted",
        priority: "urgent",
        bucketId: "b_week",
        assignees: ["나"],
        due: "2026-10-14",
        linkedTaskIds: ["q_t2"],
        labelIds: ["l_scope"],
        order: 1,
      }),
      exec("q_x_price", "단가 출처 표 정리", at, {
        status: "inProgress",
        priority: "important",
        bucketId: "b_week",
        assignees: ["송DB"],
        due: "2026-10-24",
        linkedTaskIds: ["q_t3"],
        labelIds: ["l_price"],
        order: 2,
      }),
      exec("q_x_form", "견적서 양식 초안", at, {
        status: "notStarted",
        priority: "low",
        bucketId: "b_later",
        assignees: ["최기획"],
        due: "2026-10-28",
        linkedTaskIds: ["q_t4"],
        labelIds: ["l_doc"],
        order: 0,
      }),
    ],
    planner: {
      buckets: [
        { id: "b_backlog", name: "백로그" },
        { id: "b_week", name: "이번 주" },
        { id: "b_later", name: "다음" },
        { id: "b_done", name: "끝난 일" },
      ],
      labels: [
        { id: "l_scope", name: "범위", color: "purple" },
        { id: "l_price", name: "단가", color: "teal" },
        { id: "l_doc", name: "문서", color: "blue" },
      ],
      customFields: [],
    },
    records: quoteRecords(),
    documents: quoteDocuments(),
    documentLinks: quoteLinks(),
    resourceAssignments: quoteAssignments(),
    solutions: quoteSolutions(),
    budget: quoteBudget(),
  });
}

function quoteRecords(): ProjectRecord[] {
  return [
    record("q_iss_price", "issue", 1, "단가 테이블에 작년 계약 단가가 섞여 있음", "2026-10-16T01:00:00.000Z", "송DB", {
      status: "inProgress",
      severity: "high",
      source: "internal",
      assignees: ["송DB"],
      due: "2026-10-24",
      impacts: ["cost", "quality"],
      sections: {
        summary: "2025년 단가와 올해 합의 단가가 한 표에 있습니다.",
        cause: "이관 파일이 계약 연도를 구분하지 않습니다.",
      },
      linkedTaskIds: ["q_t3"],
      linkedExecutionTaskIds: ["q_x_price"],
    }),
    record("q_dec_split", "decision", 1, "변경 항목은 본 견적과 분리한다", "2026-10-13T02:00:00.000Z", "나", {
      status: "accepted",
      source: "internal",
      assignees: ["나", "최기획"],
      due: "2026-10-13",
      sections: {
        context: "현장 추가 요청이 본 견적 합계를 매주 바꿉니다.",
        decision: "본 견적은 착수 범위를 잠그고, 추가 요청은 변경 계약 표로 넘깁니다.",
        alternatives: "한 표에 두고 상태만 나누기 — 고객 결재선이 섞여 제외.",
      },
      linkedTaskIds: ["q_t2"],
      linkedExecutionTaskIds: ["q_x_scope"],
    }),
    record("q_chg_slack", "change", 1, "승인 알림을 메일에서 슬랙으로", "2026-10-18T03:00:00.000Z", "강인프라", {
      status: "requested",
      severity: "low",
      source: "client",
      assignees: ["강인프라"],
      due: "2026-10-30",
      impacts: ["scope"],
      sections: {
        summary: "견적 승인 요청을 고객사 슬랙 채널로 보냅니다.",
        reason: "메일 결재가 평균 이틀 밀립니다.",
        impactAnalysis: "알림 채널 하나. 본문 양식은 그대로입니다.",
      },
      linkedTaskIds: ["q_t5"],
    }),
  ];
}

function quoteDocuments(): PlanDocument[] {
  return [
    sampleDoc({
      id: "q_doc_meet",
      seq: 1,
      title: "견적 범위 회의",
      kind: "meeting",
      visibility: "plan",
      authorId: "최기획",
      heldAt: "2026-10-13",
      createdAt: "2026-10-13T02:00:00.000Z",
      updatedAt: "2026-10-13T06:00:00.000Z",
      blocks: [
        heading("q_b1", "경계"),
        paragraph("q_b2", "본 견적에 넣을 항목과 변경 계약으로 넘길 항목을 갈랐습니다."),
        bullet("q_b3", "작년 단가는 올해 합의 단가로 바꾸기 전에 견적에 넣지 않습니다."),
        checkItem("q_b4", "변경 항목 목록을 고객에 보낸다", false),
      ],
      attachments: [fileAttachment("q_doc_att_meet", "견적 항목 목록.pdf", "2026-10-13T03:00:00.000Z", "최기획")],
      comments: [{ id: "q_doc_cm1", author: "나", body: "범위 메모는 오늘 안에 본문에 반영하겠습니다.", at: "2026-10-13T06:00:00.000Z" }],
    }),
    sampleDoc({
      id: "q_doc_weekly",
      seq: 2,
      title: "견적 자동화 주간 보고",
      kind: "memo",
      visibility: "plan",
      authorId: "나",
      createdAt: "2026-10-20T03:00:00.000Z",
      blocks: [
        heading("q_w1", "이번 주"),
        paragraphMention("q_w2", "범위 정리는 ", "executionTask", "q_x_scope", "에 두고, 단가 혼입은 이슈로 따로 봅니다."),
        bullet("q_w3", "단가 테이블은 25%입니다. 작년 행을 빼는 중이어서 양식 착수는 다음 주입니다."),
        quoteBlock("q_w4", "파일럿 오픈은 1월 29일 가정을 유지합니다."),
      ],
      attachments: [linkAttachment("q_doc_att_weekly", "주간 보고 초안", "https://example.com/reports/quote-w43", "2026-10-20T03:10:00.000Z", "나")],
    }),
    sampleDoc({
      id: "q_doc_price",
      seq: 3,
      title: "단가 출처 메모",
      kind: "material",
      visibility: "private",
      authorId: "나",
      createdAt: "2026-10-17T04:00:00.000Z",
      blocks: [paragraph("q_pnote", "영업 확정 단가만 견적에 쓰고, 가견적은 열을 나눠 숨깁니다.")],
      attachments: [imageAttachment("q_doc_att_price", "단가 열 스케치.png", "2026-10-17T04:10:00.000Z", "나")],
    }),
  ];
}

function quoteLinks(): DocumentLink[] {
  return [
    docLink({
      id: "q_dl_1",
      documentId: "q_doc_meet",
      targetKind: "executionTask",
      targetId: "q_x_scope",
      origins: ["explicit"],
      title: "견적 범위 메모 정리",
      createdAt: "2026-10-13T02:20:00.000Z",
      createdBy: "최기획",
    }),
    docLink({
      id: "q_dl_2",
      documentId: "q_doc_meet",
      targetKind: "record",
      targetId: "q_dec_split",
      origins: ["explicit"],
      code: "DEC-1",
      title: "변경 항목은 본 견적과 분리한다",
      createdAt: "2026-10-13T02:30:00.000Z",
      createdBy: "최기획",
    }),
    docLink({
      id: "q_dl_3",
      documentId: "q_doc_weekly",
      targetKind: "executionTask",
      targetId: "q_x_scope",
      origins: ["mention"],
      title: "견적 범위 메모 정리",
      blockId: "q_w2",
      createdAt: "2026-10-20T03:10:00.000Z",
      createdBy: "나",
    }),
    docLink({
      id: "q_dl_4",
      documentId: "q_doc_weekly",
      targetKind: "record",
      targetId: "q_iss_price",
      origins: ["explicit"],
      code: "ISS-1",
      title: "단가 테이블에 작년 계약 단가가 섞여 있음",
      createdAt: "2026-10-20T03:20:00.000Z",
      createdBy: "나",
    }),
    docLink({
      id: "q_dl_5",
      documentId: "q_doc_price",
      targetKind: "executionTask",
      targetId: "q_x_price",
      origins: ["explicit"],
      title: "단가 출처 표 정리",
      createdAt: "2026-10-17T04:20:00.000Z",
      createdBy: "나",
    }),
  ];
}

function quoteAssignments(): ProjectResourceAssignment[] {
  return [
    { id: "asg_q_me", resourceId: "res_me", projectRole: "사업 소유자", accessRole: "admin", startDate: "2026-10-06", endDate: "2027-01-30", allocationRatio: 0.25, plannedMM: 1.0 },
    { id: "asg_q_choi", resourceId: "res_choi_plan", projectRole: "견적 범위", accessRole: "pl", startDate: "2026-10-06", endDate: "2026-12-15", allocationRatio: 0.3, plannedMM: 0.7 },
    { id: "asg_q_song", resourceId: "res_song_dba", projectRole: "단가 테이블", accessRole: "member", startDate: "2026-10-13", endDate: "2027-01-15", allocationRatio: 0.3, plannedMM: 0.9 },
    { id: "asg_q_kang", resourceId: "res_kang_infra", projectRole: "승인 알림", accessRole: "member", startDate: "2026-10-20", endDate: "2026-11-30", allocationRatio: 0.2, plannedMM: 0.3 },
    { id: "asg_q_oh", resourceId: "res_oh_qa", projectRole: "샘플 대조", accessRole: "member", startDate: "2026-12-01", endDate: "2027-01-30", allocationRatio: 0.3, plannedMM: 0.6 },
  ];
}

function quoteSolutions(): SolutionItem[] {
  return [
    {
      id: "sol_q_sheet",
      name: "견적 양식 라이선스",
      category: "saas",
      vendor: "포시에스",
      billingCycle: "yearly",
      unitCost: 4800000,
      quantity: 1,
      totalCost: 4800000,
      startDate: "2026-10-15",
      endDate: "2027-10-14",
      assignedResourceId: "res_choi_plan",
      status: "contracted",
      notes: "견적서 PDF 출력",
    },
    {
      id: "sol_q_slack",
      name: "승인 알림 연동",
      category: "license",
      vendor: "Slack",
      billingCycle: "monthly",
      unitCost: 120000,
      quantity: 4,
      totalCost: 480000,
      startDate: "2026-11-01",
      endDate: "2027-02-28",
      assignedResourceId: "res_kang_infra",
      status: "planned",
    },
  ];
}

function quoteBudget(): ProjectBudget {
  return {
    totalBudget: 86000000,
    targetLaborCost: 62000000,
    targetSolutionCost: 6000000,
    targetExpenseCost: 10000000,
    contingencyCost: 8000000,
    costOptions: costOptions(),
  };
}

/** AI 로봇 공장의 두 번째 플랜. 라인 구축과 따로, 파일럿 셀 인증만 봅니다. */
export function createSampleSafetyProject(): Project {
  const { tasks, dependencies } = schedule(
    [
      {
        id: "s_p1",
        name: "기준",
        percent: 85,
        leaves: [
          { id: "s_t1", name: "인증 범위 확정", duration: 4, percentComplete: 100, assignee: "신자문", assignedResourceId: "res_shin_spec", plannedMM: 0.15, deliverable: "인증 범위 v1" },
          { id: "s_t2", name: "안전펜스 규격 대조", duration: 6, percentComplete: 70, assignee: "백제어", assignedResourceId: "res_baek_ctrl", plannedMM: 0.3 },
          { id: "s_m1", name: "기준 잠금", duration: 0, percentComplete: 100 },
        ],
      },
      {
        id: "s_p2",
        name: "증빙",
        percent: 20,
        leaves: [
          { id: "s_t3", name: "위험성 평가", duration: 8, percentComplete: 40, assignee: "백제어", assignedResourceId: "res_baek_ctrl", plannedMM: 0.4 },
          { id: "s_t4", name: "비상정지 시나리오", duration: 5, percentComplete: 0, assignee: "오품질", assignedResourceId: "res_oh_qa", plannedMM: 0.25 },
          { id: "s_t5", name: "2인 재가동 절차", duration: 4, percentComplete: 20, assignee: "나", assignedResourceId: "res_me", plannedMM: 0.15 },
          { id: "s_t8", name: "도면 개정 확인", duration: 4, percentComplete: 55, assignee: "백제어", assignedResourceId: "res_baek_ctrl", plannedMM: 0.2, deliverable: "개정 도면 v2", constraint: { type: "SNET", date: "2026-10-12" } },
          { id: "s_t9", name: "보호구 점검", duration: 3, percentComplete: 40, assignee: "오품질", assignedResourceId: "res_oh_qa", plannedMM: 0.15, deliverable: "보호구 점검표", constraint: { type: "SNET", date: "2026-10-13" } },
          { id: "s_t10", name: "작업자 면담", duration: 3, percentComplete: 25, assignee: "신자문", assignedResourceId: "res_shin_spec", plannedMM: 0.1, constraint: { type: "SNET", date: "2026-10-14" } },
          { id: "s_t11", name: "펜스 높이 재실측", duration: 5, percentComplete: 35, assignee: "백제어", assignedResourceId: "res_baek_ctrl", plannedMM: 0.25, deliverable: "실측표 v2", constraint: { type: "SNET", date: "2026-10-16" } },
          { id: "s_t12", name: "인터록 점검", duration: 4, percentComplete: 50, assignee: "오품질", assignedResourceId: "res_oh_qa", plannedMM: 0.2, deliverable: "인터록 기록", constraint: { type: "SNET", date: "2026-10-19" } },
          { id: "s_t13", name: "교육 명단 정리", duration: 3, percentComplete: 15, assignee: "나", assignedResourceId: "res_me", plannedMM: 0.1, deliverable: "교육 명단 v0.4", constraint: { type: "SNET", date: "2026-10-20" } },
          { id: "s_t14", name: "증빙 묶음 정리", duration: 8, percentComplete: 40, assignee: "신자문", assignedResourceId: "res_shin_spec", plannedMM: 0.3, deliverable: "증빙 목록 v0.6", constraint: { type: "SNET", date: "2026-10-19" } },
          { id: "s_t15", name: "비상정지 리허설", duration: 4, percentComplete: 0, assignee: "오품질", assignedResourceId: "res_oh_qa", plannedMM: 0.2, deliverable: "리허설 체크리스트", constraint: { type: "SNET", date: "2026-10-26" } },
          { id: "s_t16", name: "심사 자료 묶음", duration: 5, percentComplete: 0, assignee: "신자문", assignedResourceId: "res_shin_spec", plannedMM: 0.25, deliverable: "제출 묶음 v0.3", constraint: { type: "SNET", date: "2026-10-27" } },
          { id: "s_t17", name: "현장 교육", duration: 3, percentComplete: 0, assignee: "나", assignedResourceId: "res_me", plannedMM: 0.15, constraint: { type: "SNET", date: "2026-10-28" } },
          { id: "s_t18", name: "잔여 위험 등록", duration: 4, percentComplete: 10, assignee: "백제어", assignedResourceId: "res_baek_ctrl", plannedMM: 0.2, deliverable: "위험 등록부", constraint: { type: "SNET", date: "2026-10-29" } },
          { id: "s_m2", name: "증빙 제출", duration: 0, assignee: "신자문", assignedResourceId: "res_shin_spec", constraint: { type: "SNET", date: "2026-10-20" } },
          { id: "s_m3", name: "현장 점검", duration: 0, assignee: "백제어", assignedResourceId: "res_baek_ctrl", constraint: { type: "SNET", date: "2026-10-22" } },
          { id: "s_m4", name: "증빙 내부 검토", duration: 0, assignee: "신자문", assignedResourceId: "res_shin_spec", constraint: { type: "SNET", date: "2026-10-28" } },
          { id: "s_m5", name: "심사 자료 마감", duration: 0, assignee: "나", assignedResourceId: "res_me", constraint: { type: "SNET", date: "2026-11-04" } },
        ],
      },
      {
        id: "s_p3",
        name: "심사",
        leaves: [
          { id: "s_t6", name: "모의 심사", duration: 5, assignee: "민테스터", assignedResourceId: "res_min_qa", plannedMM: 0.25, constraint: { type: "SNET", date: "2027-01-05" } },
          { id: "s_t7", name: "인증 취득", duration: 0, deadline: "2027-02-27" },
        ],
      },
    ],
    [
      ["s_t1", "s_t2"],
      ["s_t2", "s_m1"],
      ["s_m1", "s_t3"],
      ["s_t3", "s_t4", "SS", 3],
      ["s_t3", "s_t5"],
      ["s_t4", "s_m2"],
      ["s_t5", "s_m2"],
      ["s_m2", "s_t6"],
      ["s_t6", "s_t7"],
    ],
    "s",
  );
  const at = "2026-10-08T01:00:00.000Z";
  return finish({
    id: "sample-robot-safety",
    name: "안전 인증",
    start: "2026-09-14",
    statusDate: "2026-10-20",
    tasks,
    dependencies,
    executionTasks: [
      exec("s_x_list", "인증 체크리스트 초안", at, {
        status: "inProgress",
        priority: "important",
        bucketId: "b_week",
        assignees: ["나"],
        start: "2026-10-19",
        due: "2026-10-21",
        linkedTaskIds: ["s_t5"],
        labelIds: ["l_cert"],
        order: 0,
      }),
      exec("s_x_photo", "펜스 실측 사진", at, {
        status: "notStarted",
        priority: "urgent",
        bucketId: "b_week",
        assignees: ["나"],
        due: "2026-10-10",
        linkedTaskIds: ["s_t2"],
        labelIds: ["l_fence"],
        order: 1,
      }),
      exec("s_x_draw", "도면과 현장 대조", at, {
        status: "inProgress",
        priority: "important",
        bucketId: "b_week",
        assignees: ["백제어"],
        due: "2026-10-23",
        linkedTaskIds: ["s_t2"],
        labelIds: ["l_fence"],
        order: 2,
      }),
      exec("s_x_stop", "비상정지 시나리오 초안", at, {
        status: "notStarted",
        priority: "important",
        bucketId: "b_later",
        assignees: ["오품질"],
        due: "2026-11-06",
        linkedTaskIds: ["s_t4"],
        labelIds: ["l_cert"],
        order: 0,
      }),
    ],
    planner: {
      buckets: [
        { id: "b_backlog", name: "백로그" },
        { id: "b_week", name: "이번 주 현장" },
        { id: "b_later", name: "심사 전" },
        { id: "b_done", name: "끝난 일" },
      ],
      labels: [
        { id: "l_cert", name: "인증", color: "red" },
        { id: "l_fence", name: "펜스", color: "orange" },
        { id: "l_doc", name: "문서", color: "blue" },
      ],
      customFields: [],
    },
    records: safetyRecords(),
    documents: safetyDocuments(),
    documentLinks: safetyLinks(),
    resourceAssignments: safetyAssignments(),
    solutions: safetySolutions(),
    budget: safetyBudget(),
  });
}

function safetyRecords(): ProjectRecord[] {
  return [
    record("s_iss_fence", "issue", 1, "안전펜스 높이 도면이 현장과 다름", "2026-10-15T01:00:00.000Z", "백제어", {
      status: "inProgress",
      severity: "high",
      source: "vendor",
      assignees: ["백제어"],
      due: "2026-10-23",
      impacts: ["schedule", "quality"],
      sections: {
        summary: "도면은 1,800mm이고 설치분은 1,600mm입니다.",
        cause: "파일럿 셀 바닥 덕트가 도면에 없습니다.",
      },
      linkedTaskIds: ["s_t2"],
      linkedExecutionTaskIds: ["s_x_draw", "s_x_photo"],
      attachments: [imageAttachment("s_att_fence", "펜스 실측.jpg", "2026-10-15T02:00:00.000Z", "백제어")],
    }),
    record("s_rsk_date", "risk", 1, "심사가 파일럿 셀 가동보다 앞설 수 있음", "2026-10-09T02:00:00.000Z", "신자문", {
      status: "mitigating",
      severity: "medium",
      source: "internal",
      assignees: ["신자문"],
      due: "2026-11-14",
      impacts: ["schedule"],
      sections: {
        summary: "인증 기관 일정이 11월로 당겨질 수 있습니다.",
        mitigation: "모의 심사를 증빙 제출 직후로 당기고, 양산 셀은 범위에서 뺍니다.",
      },
      linkedTaskIds: ["s_t6"],
    }),
    record("s_dec_two", "decision", 1, "재가동은 2인 확인", "2026-10-08T04:00:00.000Z", "나", {
      status: "accepted",
      source: "regulation",
      assignees: ["나", "백제어"],
      due: "2026-10-08",
      sections: {
        context: "비상 정지 이후 한 사람 재가동이 절차에 남아 있습니다.",
        decision: "정지 해제와 재가동은 서로 다른 두 사람이 확인합니다.",
        consequences: "절차 문서와 교육 명단을 고칩니다. 설비 변경은 없습니다.",
      },
      linkedTaskIds: ["s_t5"],
      linkedExecutionTaskIds: ["s_x_list"],
    }),
    record("s_iss_lock", "issue", 2, "인터록 시험 장비가 현장에 없음", "2026-10-16T01:00:00.000Z", "오품질", {
      status: "inProgress",
      severity: "medium",
      source: "internal",
      assignees: ["오품질"],
      due: "2026-10-28",
      impacts: ["schedule"],
      sections: { summary: "시험 장비 반입이 10월 말로 밀렸습니다.", cause: "반입 승인이 보안 검토에 걸려 있습니다." },
      linkedTaskIds: ["s_t12"],
    }),
    record("s_iss_night", "issue", 3, "교육 대상에서 야간조가 빠짐", "2026-10-18T03:00:00.000Z", "나", {
      status: "open",
      severity: "high",
      source: "internal",
      assignees: ["나"],
      due: "2026-10-30",
      impacts: ["quality"],
      sections: { summary: "명단에 주간조만 있습니다. 야간 2개 조가 빠졌습니다." },
      linkedTaskIds: ["s_t13", "s_t17"],
    }),
    record("s_rsk_cobot", "risk", 2, "협동로봇이 인증 범위에 포함될지 미정", "2026-10-10T02:00:00.000Z", "신자문", {
      status: "open",
      severity: "low",
      source: "client",
      assignees: ["신자문"],
      due: "2026-11-06",
      impacts: ["scope"],
      sections: { summary: "발주가 파일럿 셀의 협동로봇을 범위에 넣을지 아직 정하지 않았습니다.", mitigation: "이번 심사는 펜스 안 설비만 두고, 협동로봇은 별도 항목으로 뺍니다." },
      linkedTaskIds: ["s_t3"],
    }),
    record("s_chg_fence", "change", 1, "펜스 높이를 1,800mm로 재시공", "2026-10-16T05:00:00.000Z", "백제어", {
      status: "requested",
      source: "vendor",
      assignees: ["백제어"],
      due: "2026-10-30",
      impacts: ["schedule", "cost"],
      sections: { impactAnalysis: "하부 덕트 구간 4m를 다시 세웁니다. 심사 전 완료가 필요합니다." },
      linkedTaskIds: ["s_t11"],
    }),
    record("s_chg_date", "change", 2, "심사일을 11월 둘째 주로", "2026-10-17T02:00:00.000Z", "신자문", {
      status: "reviewing",
      source: "client",
      assignees: ["신자문"],
      due: "2026-10-24",
      impacts: ["schedule"],
      sections: { impactAnalysis: "인증 기관 일정이 앞으로 당겨지면 증빙 제출도 같이 앞당깁니다." },
      linkedTaskIds: ["s_t6"],
    }),
    record("s_dec_night", "decision", 2, "야간조 교육을 주간 교육에 포함", "2026-10-18T06:00:00.000Z", "나", {
      status: "proposed",
      source: "internal",
      assignees: ["나", "오품질"],
      due: "2026-10-27",
      impacts: ["quality"],
      sections: { context: "야간조만 따로 부르면 라인이 섭니다.", decision: "주간 교육에 야간조 대표를 넣고, 전달 교육으로 나머지 조를 덮습니다.", impactAnalysis: "주간 교육 한 번으로 야간 2개 조를 덮습니다." },
      linkedTaskIds: ["s_t17"],
    }),
  ];
}

function safetyDocuments(): PlanDocument[] {
  return [
    sampleDoc({
      id: "s_doc_meet",
      seq: 1,
      title: "인증 범위 회의",
      kind: "meeting",
      visibility: "plan",
      authorId: "신자문",
      heldAt: "2026-09-18",
      createdAt: "2026-09-18T02:00:00.000Z",
      updatedAt: "2026-10-08T04:00:00.000Z",
      blocks: [
        heading("s_b1", "범위"),
        paragraph("s_b2", "이번 인증은 파일럿 1셀입니다. 양산 3셀은 셀이 선 뒤에 별도 범위로 둡니다."),
        bullet("s_b3", "재가동은 2인 확인으로 절차를 고칩니다."),
        paragraphMention("s_b4", "펜스 높이를 현장에서 다시 잽니다 ", "executionTask", "s_x_photo", ""),
      ],
      attachments: [fileAttachment("s_doc_att_scope", "인증 범위 v1.pdf", "2026-09-18T03:00:00.000Z", "신자문")],
    }),
    sampleDoc({
      id: "s_doc_weekly",
      seq: 2,
      title: "안전 인증 주간 보고",
      kind: "memo",
      visibility: "plan",
      authorId: "나",
      createdAt: "2026-10-20T04:00:00.000Z",
      blocks: [
        heading("s_w1", "이번 주"),
        paragraphMention("s_w2", "체크리스트는 ", "executionTask", "s_x_list", "에 모으고, 펜스 높이 차이는 이슈로 둡니다."),
        bullet("s_w3", "위험성 평가는 40%입니다. 비상정지 시나리오는 아직 착수 전입니다."),
        quoteBlock("s_w4", "인증 취득 가정은 2027-02-27입니다."),
      ],
    }),
    sampleDoc({
      id: "s_doc_proc",
      seq: 3,
      title: "2인 재가동 절차 초안",
      kind: "material",
      visibility: "private",
      authorId: "나",
      createdAt: "2026-10-16T06:00:00.000Z",
      blocks: [paragraph("s_pnote", "정지 해제는 제어 담당, 재가동 확인은 현장 리더로 적습니다. 같은 사람이 둘 다 하지 않습니다.")],
      attachments: [linkAttachment("s_doc_att_proc", "재가동 절차 초안", "https://example.com/docs/restart", "2026-10-16T06:10:00.000Z", "나")],
    }),
    sampleDoc({
      id: "s_doc_questions",
      seq: 4,
      title: "심사 질문 메모",
      kind: "memo",
      visibility: "plan",
      authorId: "신자문",
      createdAt: "2026-10-21T01:00:00.000Z",
      blocks: [paragraph("s_q1", "양산 셀을 이번 범위에서 뺐는지, 재가동 인원이 교육 명단에 있는지를 적어 둡니다.")],
    }),
  ];
}

function safetyLinks(): DocumentLink[] {
  return [
    docLink({
      id: "s_dl_1",
      documentId: "s_doc_meet",
      targetKind: "scheduleTask",
      targetId: "s_t1",
      origins: ["explicit"],
      title: "인증 범위 확정",
      createdAt: "2026-09-18T02:20:00.000Z",
      createdBy: "신자문",
    }),
    docLink({
      id: "s_dl_2",
      documentId: "s_doc_meet",
      targetKind: "record",
      targetId: "s_dec_two",
      origins: ["explicit"],
      code: "DEC-1",
      title: "재가동은 2인 확인",
      createdAt: "2026-10-08T04:10:00.000Z",
      createdBy: "나",
      blockId: "s_b3",
    }),
    docLink({
      id: "s_dl_3",
      documentId: "s_doc_weekly",
      targetKind: "executionTask",
      targetId: "s_x_list",
      origins: ["mention"],
      title: "인증 체크리스트 초안",
      createdAt: "2026-10-20T04:10:00.000Z",
      createdBy: "나",
      blockId: "s_w2",
    }),
    docLink({
      id: "s_dl_4",
      documentId: "s_doc_weekly",
      targetKind: "record",
      targetId: "s_iss_fence",
      origins: ["explicit"],
      code: "ISS-1",
      title: "안전펜스 높이 도면이 현장과 다름",
      createdAt: "2026-10-20T04:20:00.000Z",
      createdBy: "나",
      blockId: "s_w2",
    }),
    docLink({
      id: "s_dl_5",
      documentId: "s_doc_proc",
      targetKind: "executionTask",
      targetId: "s_x_list",
      origins: ["explicit"],
      title: "인증 체크리스트 초안",
      createdAt: "2026-10-16T06:20:00.000Z",
      createdBy: "나",
      blockId: "s_pnote",
    }),
    docLink({
      id: "s_dl_6",
      documentId: "s_doc_meet",
      targetKind: "executionTask",
      targetId: "s_x_photo",
      origins: ["explicit", "mention"],
      title: "펜스 실측 사진",
      createdAt: "2026-10-15T02:30:00.000Z",
      createdBy: "백제어",
      blockId: "s_b4",
    }),
  ];
}

function safetyAssignments(): ProjectResourceAssignment[] {
  return [
    { id: "asg_s_me", resourceId: "res_me", projectRole: "사업 소유자", accessRole: "admin", startDate: "2026-09-14", endDate: "2027-02-27", allocationRatio: 0.2, plannedMM: 1.1 },
    { id: "asg_s_baek", resourceId: "res_baek_ctrl", projectRole: "안전 제어", accessRole: "pl", startDate: "2026-09-14", endDate: "2027-02-27", allocationRatio: 0.3, plannedMM: 1.6 },
    { id: "asg_s_shin", resourceId: "res_shin_spec", projectRole: "인증 자문", accessRole: "viewer", startDate: "2026-09-14", endDate: "2027-02-27", allocationRatio: 0.15, plannedMM: 0.8 },
    { id: "asg_s_oh", resourceId: "res_oh_qa", projectRole: "시나리오 검증", accessRole: "member", startDate: "2026-11-01", endDate: "2027-02-27", allocationRatio: 0.3, plannedMM: 1.2 },
    { id: "asg_s_min", resourceId: "res_min_qa", projectRole: "모의 심사", accessRole: "member", startDate: "2027-01-05", endDate: "2027-02-27", allocationRatio: 0.4, plannedMM: 0.7 },
  ];
}

function safetySolutions(): SolutionItem[] {
  return [
    {
      id: "sol_s_curtain",
      name: "라이트커튼 추가 1식",
      category: "hardware",
      vendor: "필츠코리아",
      billingCycle: "once",
      unitCost: 6400000,
      quantity: 1,
      totalCost: 6400000,
      startDate: "2026-11-01",
      assignedResourceId: "res_baek_ctrl",
      status: "planned",
      notes: "파일럿 셀 출입구",
    },
    {
      id: "sol_s_audit",
      name: "안전 인증 심사 비용",
      category: "license",
      vendor: "한국산업안전보건공단",
      billingCycle: "once",
      unitCost: 9000000,
      quantity: 1,
      totalCost: 9000000,
      startDate: "2027-01-15",
      assignedResourceId: "res_shin_spec",
      status: "contracted",
    },
  ];
}

function safetyBudget(): ProjectBudget {
  return {
    totalBudget: 140000000,
    targetLaborCost: 98000000,
    targetSolutionCost: 16000000,
    targetExpenseCost: 14000000,
    contingencyCost: 12000000,
    costOptions: costOptions(),
  };
}

/** 그로스 마케팅의 두 번째 플랜. 시스템 구축과 따로 채널 콘텐츠를 돌립니다. */
export function createSampleContentProject(): Project {
  const { tasks, dependencies } = schedule(
    [
      {
        id: "c_p1",
        name: "캘린더",
        percent: 55,
        leaves: [
          { id: "c_t1", name: "채널별 톤 정리", duration: 4, percentComplete: 80, assignee: "장카피", assignedResourceId: "res_jang_copy", plannedMM: 0.2, deliverable: "톤 표 v0.9" },
          { id: "c_t2", name: "10월 캘린더 확정", duration: 3, percentComplete: 40, assignee: "나", assignedResourceId: "res_me", plannedMM: 0.15 },
          { id: "c_m1", name: "캘린더 승인", duration: 0 },
        ],
      },
      {
        id: "c_p2",
        name: "제작",
        percent: 10,
        leaves: [
          { id: "c_t3", name: "온보딩 시퀀스 카피", duration: 6, percentComplete: 30, assignee: "장카피", assignedResourceId: "res_jang_copy", plannedMM: 0.3 },
          { id: "c_t4", name: "랜딩 섹션 문구", duration: 5, percentComplete: 0, assignee: "정프론트", assignedResourceId: "res_jung_front", plannedMM: 0.25 },
          { id: "c_t5", name: "실험 카피 2종", duration: 4, percentComplete: 0, assignee: "윤데이터", assignedResourceId: "res_yoon_data", plannedMM: 0.2 },
          { id: "c_m2", name: "제작 마감", duration: 0 },
        ],
      },
      {
        id: "c_p3",
        name: "배포",
        leaves: [
          { id: "c_t6", name: "검수", duration: 4, assignee: "민테스터", assignedResourceId: "res_min_qa", plannedMM: 0.2, constraint: { type: "SNET", date: "2026-11-16" } },
          { id: "c_t7", name: "배포", duration: 0, deadline: "2026-12-18" },
        ],
      },
    ],
    [
      ["c_t1", "c_t2", "SS", 1],
      ["c_t2", "c_m1"],
      ["c_m1", "c_t3"],
      ["c_t3", "c_t4", "SS", 2],
      ["c_t3", "c_t5"],
      ["c_t4", "c_m2"],
      ["c_t5", "c_m2"],
      ["c_m2", "c_t6"],
      ["c_t6", "c_t7"],
    ],
    "c",
  );
  const at = "2026-10-10T01:00:00.000Z";
  return finish({
    id: "sample-mkt-content",
    name: "콘텐츠 운영",
    start: "2026-10-06",
    statusDate: "2026-10-20",
    tasks,
    dependencies,
    executionTasks: [
      exec("c_x_cal", "이번 주 콘텐츠 캘린더", at, {
        status: "inProgress",
        priority: "important",
        bucketId: "b_week",
        assignees: ["나"],
        start: "2026-10-19",
        due: "2026-10-19",
        linkedTaskIds: ["c_t2"],
        labelIds: ["l_cal"],
        order: 0,
      }),
      exec("c_x_tone", "톤 가이드 차이 정리", at, {
        status: "notStarted",
        priority: "urgent",
        bucketId: "b_week",
        assignees: ["장카피"],
        due: "2026-10-15",
        linkedTaskIds: ["c_t1"],
        labelIds: ["l_copy"],
        notes: "앱 푸시와 메일 톤이 다릅니다.",
        order: 1,
      }),
      exec("c_x_copy", "온보딩 3통 초안", at, {
        status: "inProgress",
        priority: "important",
        bucketId: "b_week",
        assignees: ["장카피"],
        due: "2026-10-27",
        linkedTaskIds: ["c_t3"],
        labelIds: ["l_copy"],
        order: 2,
      }),
      exec("c_x_land", "랜딩 문구 자리", at, {
        status: "notStarted",
        priority: "low",
        bucketId: "b_later",
        assignees: ["정프론트"],
        due: "2026-11-04",
        linkedTaskIds: ["c_t4"],
        labelIds: ["l_page"],
        order: 0,
      }),
    ],
    planner: {
      buckets: [
        { id: "b_backlog", name: "아이디어" },
        { id: "b_week", name: "이번 주" },
        { id: "b_later", name: "다음 배포" },
        { id: "b_done", name: "나간 글" },
      ],
      labels: [
        { id: "l_cal", name: "캘린더", color: "purple" },
        { id: "l_copy", name: "카피", color: "orange" },
        { id: "l_page", name: "화면", color: "blue" },
      ],
      customFields: [],
    },
    records: contentRecords(),
    documents: contentDocuments(),
    documentLinks: contentLinks(),
    resourceAssignments: contentAssignments(),
    solutions: contentSolutions(),
    budget: contentBudget(),
  });
}

function contentRecords(): ProjectRecord[] {
  return [
    record("c_iss_tone", "issue", 1, "브랜드 톤이 채널마다 다름", "2026-10-14T01:00:00.000Z", "장카피", {
      status: "open",
      severity: "medium",
      source: "internal",
      assignees: ["장카피"],
      due: "2026-10-22",
      impacts: ["quality"],
      sections: {
        summary: "앱 푸시는 구어체이고 메일은 경어입니다.",
        cause: "채널 가이드가 캠페인마다 따로 있습니다.",
      },
      linkedTaskIds: ["c_t1"],
      linkedExecutionTaskIds: ["c_x_tone"],
    }),
    record("c_dec_split", "decision", 1, "실험 카피는 본 카피와 다른 문서에 둔다", "2026-10-16T02:00:00.000Z", "나", {
      status: "accepted",
      source: "internal",
      assignees: ["나", "윤데이터"],
      due: "2026-10-16",
      sections: {
        context: "실험 문장이 본 시퀀스 초안을 덮어씁니다.",
        decision: "본 카피와 실험 카피는 문서를 나눕니다. 이긴 문장만 본 문서로 옮깁니다.",
      },
      linkedTaskIds: ["c_t5"],
    }),
    record("c_chg_winback", "change", 1, "10월 캘린더에 휴면 고객 윈백 1건 추가", "2026-10-18T03:00:00.000Z", "권CRM", {
      status: "reviewing",
      severity: "low",
      source: "client",
      assignees: ["권CRM"],
      due: "2026-10-24",
      impacts: ["scope"],
      sections: {
        summary: "90일 미구매 고객 메일 1통을 10월 넷째 주에 넣습니다.",
        reason: "세일즈가 파일럿에 윈백을 보고 싶어 합니다.",
        impactAnalysis: "카피 1통과 검수 반나절. 시스템 변경은 없습니다.",
      },
      linkedTaskIds: ["c_t2"],
      linkedExecutionTaskIds: ["c_x_cal"],
    }),
  ];
}

function contentDocuments(): PlanDocument[] {
  return [
    sampleDoc({
      id: "c_doc_meet",
      seq: 1,
      title: "10월 콘텐츠 캘린더 회의",
      kind: "meeting",
      visibility: "plan",
      authorId: "나",
      heldAt: "2026-10-09",
      createdAt: "2026-10-09T02:00:00.000Z",
      updatedAt: "2026-10-09T05:00:00.000Z",
      blocks: [
        heading("c_b1", "10월"),
        paragraph("c_b2", "온보딩 3통과 랜딩 문구를 먼저 잠그고, 실험 카피는 그 다음 주에 둡니다."),
        bullet("c_b3", "채널 톤이 다르면 이번 주 안에 한 표로 모읍니다."),
        checkItem("c_b4", "휴면 고객 윈백을 넷째 주에 넣을지 세일즈 확인", false),
      ],
      attachments: [fileAttachment("c_doc_att_cal", "10월 콘텐츠 캘린더.pdf", "2026-10-09T03:00:00.000Z", "나")],
      comments: [{ id: "c_doc_cm1", author: "장카피", body: "메일 톤을 앱에 맞추면 문장이 짧아집니다.", at: "2026-10-09T05:00:00.000Z" }],
    }),
    sampleDoc({
      id: "c_doc_weekly",
      seq: 2,
      title: "콘텐츠 주간 보고",
      kind: "memo",
      visibility: "plan",
      authorId: "나",
      createdAt: "2026-10-20T05:00:00.000Z",
      blocks: [
        heading("c_w1", "이번 주"),
        paragraphMention("c_w2", "캘린더는 ", "executionTask", "c_x_cal", "에서 고치고, 톤 차이는 이슈로 남깁니다."),
        bullet("c_w3", "온보딩 초안은 3통 중 1통까지 왔습니다."),
        quoteBlock("c_w4", "배포는 12월 18일 가정입니다. 윈백 1통이 확정되면 검수만 늘어납니다."),
      ],
      attachments: [linkAttachment("c_doc_att_weekly", "콘텐츠 주간 메모", "https://example.com/reports/content-w43", "2026-10-20T05:10:00.000Z", "나")],
    }),
    sampleDoc({
      id: "c_doc_copy",
      seq: 3,
      title: "온보딩 메일 문장 초안",
      kind: "material",
      visibility: "private",
      authorId: "나",
      createdAt: "2026-10-18T07:00:00.000Z",
      blocks: [
        paragraph("c_c1", "D+0는 가입 확인, D+2는 첫 세그먼트, D+5는 템플릿 추천입니다. 실험 문장은 이 문서에 넣지 않습니다."),
      ],
      attachments: [imageAttachment("c_doc_att_copy", "메일 시안.png", "2026-10-18T07:10:00.000Z", "나")],
    }),
  ];
}

function contentLinks(): DocumentLink[] {
  return [
    docLink({
      id: "c_dl_1",
      documentId: "c_doc_meet",
      targetKind: "executionTask",
      targetId: "c_x_cal",
      origins: ["explicit"],
      title: "이번 주 콘텐츠 캘린더",
      createdAt: "2026-10-09T02:20:00.000Z",
      createdBy: "나",
    }),
    docLink({
      id: "c_dl_2",
      documentId: "c_doc_meet",
      targetKind: "record",
      targetId: "c_iss_tone",
      origins: ["explicit"],
      code: "ISS-1",
      title: "브랜드 톤이 채널마다 다름",
      createdAt: "2026-10-14T01:30:00.000Z",
      createdBy: "장카피",
    }),
    docLink({
      id: "c_dl_3",
      documentId: "c_doc_weekly",
      targetKind: "executionTask",
      targetId: "c_x_cal",
      origins: ["mention"],
      title: "이번 주 콘텐츠 캘린더",
      createdAt: "2026-10-20T05:10:00.000Z",
      createdBy: "나",
      blockId: "c_w2",
    }),
    docLink({
      id: "c_dl_4",
      documentId: "c_doc_weekly",
      targetKind: "scheduleTask",
      targetId: "c_t3",
      origins: ["explicit"],
      title: "온보딩 시퀀스 카피",
      createdAt: "2026-10-20T05:20:00.000Z",
      createdBy: "나",
    }),
    docLink({
      id: "c_dl_5",
      documentId: "c_doc_copy",
      targetKind: "executionTask",
      targetId: "c_x_copy",
      origins: ["explicit"],
      title: "온보딩 3통 초안",
      createdAt: "2026-10-18T07:20:00.000Z",
      createdBy: "나",
    }),
  ];
}

function contentAssignments(): ProjectResourceAssignment[] {
  return [
    { id: "asg_c_me", resourceId: "res_me", projectRole: "사업 소유자", accessRole: "admin", startDate: "2026-10-06", endDate: "2026-12-18", allocationRatio: 0.2, plannedMM: 0.5 },
    { id: "asg_c_jang", resourceId: "res_jang_copy", projectRole: "카피", accessRole: "pl", startDate: "2026-10-06", endDate: "2026-12-18", allocationRatio: 0.4, plannedMM: 1.0 },
    { id: "asg_c_jung", resourceId: "res_jung_front", projectRole: "랜딩 문구 자리", accessRole: "member", startDate: "2026-10-20", endDate: "2026-12-18", allocationRatio: 0.3, plannedMM: 0.6 },
    { id: "asg_c_kwon", resourceId: "res_kwon_crm", projectRole: "윈백 검토", accessRole: "member", startDate: "2026-10-06", endDate: "2026-11-15", allocationRatio: 0.3, plannedMM: 0.4 },
    { id: "asg_c_yoon", resourceId: "res_yoon_data", projectRole: "실험 카피", accessRole: "member", startDate: "2026-10-20", endDate: "2026-12-18", allocationRatio: 0.2, plannedMM: 0.4 },
    { id: "asg_c_min", resourceId: "res_min_qa", projectRole: "검수", accessRole: "member", startDate: "2026-11-16", endDate: "2026-12-18", allocationRatio: 0.3, plannedMM: 0.3 },
  ];
}

function contentSolutions(): SolutionItem[] {
  return [
    {
      id: "sol_c_figma",
      name: "콘텐츠 시안 좌석",
      category: "saas",
      vendor: "Figma",
      billingCycle: "monthly",
      unitCost: 24000,
      quantity: 6,
      totalCost: 144000,
      startDate: "2026-10-06",
      endDate: "2026-12-31",
      assignedResourceId: "res_jung_front",
      status: "active",
      notes: "2인 × 3개월",
    },
    {
      id: "sol_c_stock",
      name: "스톡 이미지",
      category: "license",
      vendor: "셔터스톡",
      billingCycle: "once",
      unitCost: 900000,
      quantity: 1,
      totalCost: 900000,
      startDate: "2026-10-10",
      assignedResourceId: "res_jang_copy",
      status: "contracted",
    },
  ];
}

function contentBudget(): ProjectBudget {
  return {
    totalBudget: 48000000,
    targetLaborCost: 36000000,
    targetSolutionCost: 2000000,
    targetExpenseCost: 6000000,
    contingencyCost: 4000000,
    costOptions: costOptions(),
  };
}
